<script setup>
import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from 'vue'
import { useRouter }            from 'vue-router'
import { useProfileStore }      from '@/stores/profile'
import { useTranslaterStore }   from '@/stores/translater'
import { useCarinfoStore }      from '@/stores/car-info'
import { useCarsStore }         from '@/stores/cars'
import { useFavoriteCarsStore } from '@/stores/favorite-cars'
import calcDatas                from '@/helpers/calcDatas'

const router = useRouter()

import checkArrayIncludes   from '@/helpers/checkArrayIncludes'
import vinDecoder           from '@/helpers/vinDecoder'
import Modal                from '@/components/UTAModal.vue'
import UTACarImage          from '@/components/car/UTACarImage.vue'

const props = defineProps({
    uuid: {
        type: String,
        required: true
    }
})

const profileStore      = useProfileStore()
const translaterStore   = useTranslaterStore()
const carinfoStore      = useCarinfoStore()
const carsStore         = useCarsStore()
const favoriteCarsStore = useFavoriteCarsStore()

const profile           = computed(() => profileStore.profile)
const getTranslate      = computed(() => translaterStore.getTranslate)
const carinfo           = computed(() => carinfoStore.carinfo)
const carinfoUpdateMsg  = computed(() => carinfoStore.carinfoUpdateMsg)
const carsDeleteProcess = computed(() => carsStore.carsDeleteProcess)
const carsDeleteMsg     = computed(() => carsStore.carsDeleteMsg)

const manufactorer  = ref(null)
const phone         = ref(null)
const name          = ref(null)
const carNumber     = ref(null) 
const state         = ref(null)
const imei          = ref(null)
const vin           = ref(null)
const vinUpdate     = ref(null)
const vinUpdateTm   = ref(null)
const iccid         = ref(null)
const eiccid        = ref(null)
const model         = ref(null)
const year          = ref(null)
const telematic     = ref(false)
const vinUpdatedAt  = ref(null)

const editCarInfo   = ref(false)
const editPrivates  = ref(false)
const formInvalids  = {}
const formErrors    = ref([])
const showCarDelete = ref(false)
const deleteCarUUID = ref(null)

function updateCarinfoParams(carinfo) {

    if(carinfo) {

        name.value           = carinfo.name 
        carNumber.value      = carinfo.carNumber 

        if(carinfo.data) {

            const vinDecode = vinDecoder(carinfo.data.vin)

            model.value         = vinDecode.model 
            year.value          = vinDecode.year 
            manufactorer.value  = vinDecode.owner 
            year.value          = vinDecode.year 

            phone.value         = carinfo.data.phone 
            state.value         = carinfo.data.state 
            imei.value          = carinfo.data.imei 
            vin.value           = carinfo.data.vin 
            telematic.value     = carinfo.data.telematics 
            iccid.value         = carinfo.data.iccid 
            eiccid.value        = carinfo.data.eiccid
            vinUpdatedAt.value  = carinfo.createdAt ? 
                                    new Date(carinfo.createdAt * 1000).toLocaleString() : 
                                    '-'

            if(carinfo.data.vinUpdatedAt) {

                const d             = Number(carinfo.data.vinUpdatedAt) * 1000 // Приведётся автоматом, но чтобы не забывать, что дата теперь стринга
                vinUpdate.value     = new Date(d).toLocaleString()
                vinUpdateTm.value   = calcDatas('tm', d)

            } else {

                vinUpdate.value = null
                vinUpdateTm.value = null
            }
        }
    }
}

// function carImage(vin) {
//     const vinInfo = vinDecoder(vin)
//     return vinInfo.image ? 
//         new URL(vinInfo.image , import.meta.url).href : 
//         new URL(`/src/assets/images/models/unknown_lg.jpg`, import.meta.url).href
// }

function submitUpdateCarinfo(event) {

    editPrivates.value = checkArrayIncludes(profile.value.attr?.array, ["EDIT_PRIVATE_CAR", "SUPER_ADMIN"])

    formInvalids.value = {}
    formErrors.value = []

    const fields = event.target
    let payload = {
        uuid: props.uuid
    }

    payload.name        = fields.UTACarinfoName.value
    payload.carNumber   = fields.UTACarinfoCarNumber.value

    if(editPrivates.value) {

        payload.data = {}

        if(fields.UTACarinfoImei.value) {
            payload.data.imei = fields.UTACarinfoImei.value
        } else {
            formErrors.value.push(getTranslate.value.CARINFO_IMEI)
            formInvalids.value.UTACarinfoImei = true
        }

        payload.data.phone          = fields.UTACarinfoPhone.value
        payload.data.state          = fields.UTACarinfoState.value
        payload.data.vin            = fields.UTACarinfoVin.value
        payload.data.telematics     = fields.UTACarinfoTelematic.checked
        payload.data.iccid          = fields.UTACarinfoIccid.value
        payload.data.eiccid         = fields.UTACarinfoEiccid.value

        if(vinUpdateTm.value) {
            payload.data.vinUpdatedAt = String(Math.round(new Date(vinUpdateTm.value).getTime() / 1000)) 
        } else {
            payload.data.vinUpdatedAt = ''
        }
    }

    if(formErrors.value.length == 0) {
        carinfoStore.updateCarinfo(payload)
    }
}

function buildCarinfo() {
    carinfoStore.readCarinfo(props.uuid).then(carinfo => {
        if(carinfo.uuid) {
            updateCarinfoParams(carinfo)
        }
    })
}

onBeforeMount(() => {
    if(carinfo.value.uuid) updateCarinfoParams(carinfo.value)
})

onBeforeUnmount(() => {
    carinfoStore.setCarinfoUpdateMsg({})
    favoriteCarsStore.setCreateFavoriteCarMsg({})
    favoriteCarsStore.setDeleteFavoriteCarMsg({})
})

watch(carinfoUpdateMsg, msg => {
    if('success' in msg && msg.success) {
        updateCarinfoParams(carinfo.value)
        editCarInfo.value = false
    }
})

watch(carsDeleteMsg, msg => {
    if('success' in msg && msg.success) {
        router.push('/cars')
    }
})

</script>

<template>
    <form class="uta-car-info" @submit.prevent="submitUpdateCarinfo($event)">
        <header class="uta-page-header" role="heading" aria-level="1">
            <h1 class="uta-page-header__title">
                {{ getTranslate.CARINFO_HEADER }}
                <template v-if="carinfo.favorite">
                    <div 
                        :title="getTranslate.FAVORITE_CARS_ADD"
                        class="uta-car-info__favorite"
                        @click="favoriteCarsStore.deleteFavoriteCar({ uuidCarArray: [ props.uuid ] }).then(() => buildCarinfo())"
                    >
                        <span class="material-icons uta-car-info__favorite-icon">
                            star
                        </span>
                    </div>
                </template>
                <template v-else>
                    <div 
                        :title="getTranslate.FAVORITE_CARS_ADD"
                        class="uta-car-info__favorite"
                        @click="favoriteCarsStore.createFavoriteCar({ uuidCarArray: [ props.uuid ] }).then(() => buildCarinfo())"
                    >
                        <span class="material-icons uta-car-info__favorite-icon">
                            star_outline
                        </span>
                    </div>
                </template>
            </h1>
            <div class="uta-page-header__actions">
                <template v-if="!editCarInfo">
                    <button 
                        class="uta-page-header__action uta-btn uta-btn--main uta-btn--sm uta-btn--has-icon" 
                        type="button" 
                        :title="getTranslate.BTN_EDIT"
                        @click.prevent="editCarInfo = true" 
                    >
                        <span class="uta-btn__content">
                            <span class="uta-btn__icon material-icons"> edit </span>
                            <span class="uta-btn__text uta-btn__text--has-icon d-none d-md-block">{{ getTranslate.BTN_EDIT }}</span>
                        </span>
                    </button>
                </template>
                <template v-else>
                    <button
                        type="button"
                        @click.prevent="editCarInfo = false" 
                        class="uta-page-header__action uta-btn uta-btn--sm uta-btn--has-icon"
                    >
                        <span class="uta-btn__content">
                            <span class="uta-btn__icon material-icons"> cancel </span>
                            <span class="uta-btn__text uta-btn__text--has-icon d-none d-md-block">{{ getTranslate.BTN_CANCEL }}</span>
                        </span>
                    </button>
                    <button
                        type="submit"
                        class="uta-page-header__action uta-btn uta-btn--main uta-btn--sm uta-btn--has-icon"
                    >
                        <span class="uta-btn__content">
                            <span class="uta-btn__icon material-icons"> save </span>
                            <span class="uta-btn__text uta-btn__text--has-icon d-none d-md-block">{{ getTranslate.BTN_SAVE }}</span>
                        </span>
                    </button>
                </template>
            </div>
        </header>
        <div class="mb-4 uta-alert uta-alert--danger" v-if="formErrors.length">
            <span class="material-icons uta-alert__icon">error_outline</span>
            <div class="uta-alert__text">
                {{ getTranslate.EFormFields }}:
                <span class="uta-alert__text-item" v-for="(error, index) in formErrors" :key="error">
                    {{ error }}{{ index !== formErrors.length - 1 ? ', ' : '' }} 
                </span>
            </div>
            <button type="button" class="uta-alert__close" @click="formErrors = [], formInvalids = {}">
                <span class="material-icons">
                    close
                </span>
            </button>
        </div>
        <div 
            v-if="'success' in carinfoUpdateMsg"
            class="mb-4 uta-alert" 
            :class="{
                'uta-alert--success'    : carinfoUpdateMsg.success, 
                'uta-alert--danger'     : !carinfoUpdateMsg.success
            }"
        >
            <span class="material-icons uta-alert__icon">
                {{ carinfoUpdateMsg.success ? 'check_circle' : 'error_outline' }}
            </span>
            <div class="uta-alert__text">
                {{ getTranslate[carinfoUpdateMsg.text] || carinfoUpdateMsg.text }}
            </div>
            <button type="button" class="uta-alert__close" @click="carinfoStore.setCarinfoUpdateMsg({})">
                <span class="material-icons">
                    close
                </span>
            </button>
        </div>
        <div class="uta-car-info__top">
            <div class="row">
                <div class="col-12 col-md-4 d-flex align-items-center">
                    <UTACarImage :vin="vin" />
                </div>
                <div class="col-12 col-md-8">
                    <div class="row uta-car-info__top-params">
                        <div class="col-12 col-sm-6">
                            <div class="uta-car-info__top-param">
                                <div class="uta-car-info__top-param-name d-flex align-items-center">
                                    <div class="uta-car-info__top-param-name-icon">
                                        <span class="material-icons">
                                            directions_car
                                        </span>
                                    </div>
                                    <div class="uta-car-info__top-param-name-text">
                                        {{ getTranslate.CARINFO_MODEL }}
                                    </div>
                                </div>
                                <div class="uta-car-info__top-param-value">
                                    {{ model || '&mdash;' }}
                                </div>
                            </div>
                        </div>
                        <div class="col-12 col-sm-6">
                            <div class="uta-car-info__top-param">
                                <div class="uta-car-info__top-param-name d-flex align-items-center">
                                    <div class="uta-car-info__top-param-name-icon">
                                        <span class="material-icons">
                                            precision_manufacturing
                                        </span>
                                    </div>
                                    <div class="uta-car-info__top-param-name-text">
                                        {{ getTranslate.CARINFO_MANUFACTORER }}
                                    </div>
                                </div>
                                <div class="uta-car-info__top-param-value">
                                    {{ manufactorer || '&mdash;' }}
                                </div>
                            </div>
                        </div>
                        <div class="col-12 col-sm-6">
                            <div class="uta-car-info__top-param">
                                <div class="uta-car-info__top-param-name d-flex align-items-center">
                                    <div class="uta-car-info__top-param-name-icon">
                                        <span class="material-icons">
                                            calendar_month
                                        </span>
                                    </div>
                                    <div class="uta-car-info__top-param-name-text">
                                        {{ getTranslate.CARINFO_YEAR }}
                                    </div>
                                </div>
                                <div class="uta-car-info__top-param-value">
                                    {{ year || '&mdash;' }}
                                </div>
                            </div>
                        </div>
                        <div class="col-12 col-sm-6">
                            <div class="uta-car-info__top-param">
                                <div class="uta-car-info__top-param-name d-flex align-items-center">
                                    <div class="uta-car-info__top-param-name-icon">
                                        <span class="material-icons">
                                            info
                                        </span>
                                    </div>
                                    <label for="UTACarinfoVin" class="uta-car-info__top-param-name-text">
                                        {{ getTranslate.CARINFO_VIN }}
                                    </label>
                                </div>
                                <div class="uta-car-info__top-param-value">
                                    <template v-if="editCarInfo && checkArrayIncludes(profile.attr?.array, ['EDIT_PRIVATE_CAR', 'SUPER_ADMIN'])">
                                        <input 
                                            type="text"
                                            class="form-control" 
                                            :class="{ 'uta-input--invalid' : formInvalids.vin }"
                                            id="UTACarinfoVin"
                                            :value="vin"
                                        >
                                    </template>
                                    <template v-else>
                                        {{ vin || '&mdash;' }}
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="uta-car-info__props">
            <div class="row">
                <div class="col-12 col-md-6 col-lg-4">
                    <div class="uta-car-info__props-prop">
                        <label for="UTACarinfoName" class="uta-car-info__props-prop-name">
                            {{ getTranslate.CARINFO_NAME }}
                        </label>
                        <div class="uta-car-info__props-prop-value">
                            <template v-if="!editCarInfo">
                                {{ name || '&mdash;' }}
                            </template>
                            <template v-else>
                                <input 
                                    type="text"
                                    class="form-control" 
                                    :class="{ 'uta-input--invalid' : formInvalids.name }"
                                    id="UTACarinfoName"
                                    :value="name"
                                >
                            </template>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-md-6 col-lg-4">
                    <div class="uta-car-info__props-prop">
                        <label for="UTACarinfoCarNumber" class="uta-car-info__props-prop-name">
                            {{ getTranslate.CARINFO_CAR_NUMBER }}
                        </label>
                        <div class="uta-car-info__props-prop-value">
                            <template v-if="!editCarInfo">
                                {{ carNumber || '&mdash;' }}
                            </template>
                            <template v-else>
                                <input 
                                    type="text"
                                    class="form-control" 
                                    :class="{ 'uta-input--invalid' : formInvalids.carNumber }"
                                    id="UTACarinfoCarNumber"
                                    :value="carNumber"
                                >
                            </template>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-md-6 col-lg-4">
                    <div class="uta-car-info__props-prop">
                        <label for="UTACarinfoState" class="uta-car-info__props-prop-name">
                            {{ getTranslate.CARINFO_STATE }}
                        </label>
                        <div class="uta-car-info__props-prop-value">
                            <template v-if="editCarInfo && checkArrayIncludes(profile.attr?.array, ['EDIT_PRIVATE_CAR', 'SUPER_ADMIN'])">
                                <input 
                                    type="text"
                                    class="form-control" 
                                    :class="{ 'uta-input--invalid' : formInvalids.state }"
                                    id="UTACarinfoState"
                                    :value="state"
                                >
                            </template>
                            <template v-else>
                                {{ state || '&mdash;' }}
                            </template>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-md-6 col-lg-4">
                    <div class="uta-car-info__props-prop">
                        <label for="UTACarinfoImei" class="uta-car-info__props-prop-name">
                            {{ getTranslate.CARINFO_IMEI }}
                        </label>
                        <div class="uta-car-info__props-prop-value">
                            <template v-if="editCarInfo && checkArrayIncludes(profile.attr?.array, ['EDIT_PRIVATE_CAR', 'SUPER_ADMIN'])">
                                <input 
                                    type="text"
                                    class="form-control" 
                                    :class="{ 'uta-input--invalid' : formInvalids.imei }"
                                    id="UTACarinfoImei"
                                    :value="imei"
                                >
                            </template>
                            <template v-else>
                                {{ imei || '&mdash;' }}
                            </template>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-md-6 col-lg-4">
                    <div class="uta-car-info__props-prop">
                        <label for="UTACarinfoPhone" class="uta-car-info__props-prop-name">
                            {{ getTranslate.CARINFO_PHONE }}
                        </label>
                        <div class="uta-car-info__props-prop-value">
                            <template v-if="editCarInfo && checkArrayIncludes(profile.attr?.array, ['EDIT_PRIVATE_CAR', 'SUPER_ADMIN'])">
                                <input 
                                    type="text"
                                    class="form-control" 
                                    :class="{ 'uta-input--invalid' : formInvalids.phone }"
                                    id="UTACarinfoPhone"
                                    :value="phone"
                                >
                            </template>
                            <template v-else>
                                {{ phone || '&mdash;' }}
                            </template>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-md-6 col-lg-4" v-if="checkArrayIncludes(profile.attr?.array, ['EDIT_PRIVATE_CAR', 'SUPER_ADMIN'])">
                    <div class="uta-car-info__props-prop">
                        <label for="UTACarinfoIccid" class="uta-car-info__props-prop-name">
                            {{ getTranslate.CARINFO_ICCID }}
                        </label>
                        <div class="uta-car-info__props-prop-value">
                            <template v-if="!editCarInfo">
                                {{ iccid || '&mdash;' }}
                            </template>
                            <template v-else>
                                <input 
                                    type="text"
                                    class="form-control" 
                                    :class="{ 'uta-input--invalid' : formInvalids.iccid }"
                                    id="UTACarinfoIccid"
                                    :value="iccid"
                                >
                            </template>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-md-6 col-lg-4" v-if="checkArrayIncludes(profile.attr?.array, ['EDIT_PRIVATE_CAR', 'SUPER_ADMIN'])">
                    <div class="uta-car-info__props-prop">
                        <label for="UTACarinfoEiccid" class="uta-car-info__props-prop-name">
                            {{ getTranslate.CARINFO_EICCID }}
                        </label>
                        <div class="uta-car-info__props-prop-value">
                            <template v-if="!editCarInfo">
                                {{ eiccid || '&mdash;' }}
                            </template>
                            <template v-else>
                                <input 
                                    type="text"
                                    class="form-control" 
                                    :class="{ 'uta-input--invalid' : formInvalids.eiccid }"
                                    id="UTACarinfoEiccid"
                                    :value="eiccid"
                                >
                            </template>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-md-6 col-lg-4">
                    <div class="uta-car-info__props-prop">
                        <div class="uta-car-info__props-prop-name">
                            {{ getTranslate.CARINFO_UUID }}
                        </div>
                        <div class="uta-car-info__props-prop-value">
                            {{ uuid }}
                        </div>
                    </div>
                </div>
                <div class="col-12 col-md-6 col-lg-4" v-if="checkArrayIncludes(profile.attr?.array, ['EDIT_PRIVATE_CAR', 'SUPER_ADMIN'])">
                    <div class="uta-car-info__props-prop">
                        <label for="UTACarinfoVinUpdate" class="uta-car-info__props-prop-name">
                            {{ getTranslate.CARINFO_VIN_UPDATE }}
                        </label>
                        <div class="uta-car-info__props-prop-value">
                            <template v-if="!editCarInfo">
                                {{ vinUpdate || '&mdash;' }}
                            </template>
                            <template v-else>
                                <input 
                                    class="form-control" 
                                    type="datetime-local" 
                                    id="UTACarinfoVinUpdate" 
                                    @change="changeDate($event)"
                                    v-model="vinUpdateTm"
                                >
                            </template>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-md-6 col-lg-4" v-if="checkArrayIncludes(profile.attr?.array, ['EDIT_PRIVATE_CAR', 'SUPER_ADMIN'])">
                    <div class="uta-car-info__props-prop">
                        <label for="UTACarinfoTelematic" class="uta-car-info__props-prop-name">
                            {{ getTranslate.CARINFO_TELEMATIC }}
                        </label>
                        <div class="uta-car-info__props-prop-value">
                            <template v-if="!editCarInfo">
                                {{ telematic ? getTranslate.YES : getTranslate.NO }}
                            </template>
                            <template v-else>
                                <div class="switcher uta-user-create__field-attrs-attr">
                                    <input 
                                        class="switcher__input" 
                                        type="checkbox" 
                                        id="UTACarinfoTelematic" 
                                        :checked="telematic"
                                    >
                                    <label 
                                        class="switcher__label" 
                                        for="UTACarinfoTelematic"
                                    >
                                        <span class="switcher__text">{{ getTranslate.CARINFO }}</span>
                                    </label>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-md-6 col-lg-4">
                    <div class="uta-car-info__props-prop">
                        <div class="uta-car-info__props-prop-name">
                            {{ getTranslate.CARINFO_CREATED }}
                        </div>
                        <div class="uta-car-info__props-prop-value">
                            {{ vinUpdatedAt }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </form>
    <div class="uta-btns">
        <a href="#" 
            class="uta-btn uta-btn uta-btn--secondary" 
            @click.prevent="
                showCarDelete = true, 
                deleteCarUUID = props.uuid
            "
        >
            {{ getTranslate.BTN_DELETE }}
        </a>
    </div>
    <transition name="bounce">
        <Modal 
            v-if="showCarDelete"
            :header="getTranslate.CARS_DELETE"
            size="md"
            @closeModal="showCarDelete = false"
        >
            <template v-slot:body>
                <div class="uta-modal__text">
                    {{ getTranslate.CARS_DELETE_DESCR }}
                </div>
                <div class="mt-4 uta-btns">
                    <a 
                        href="#" 
                        class="uta-btn uta-btn--main"
                        @click.prevent="carsStore.deleteCar(deleteCarUUID)"
                    >
                        <template v-if="!carsDeleteProcess">
                            {{ getTranslate.BTN_DELETE }}
                        </template>
                        <template v-else>
                            <div class="spinner-border" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </template>
                    </a>
                </div>
            </template>
        </Modal>
    </transition>
</template>

<style lang="scss">
@import '@/assets/styles/scss/components/car/car-info';
</style>